import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Icon,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Snackbar,
} from '@mui/material';
import { Alert, Pagination, Skeleton } from '@mui/lab/';
import moment from 'moment';
import Axios, { Canceler } from 'axios';
import Filters, { FiltersInputsProps } from '../../../../components/Filters';
import api from '../../../../services/api';
import styles from './styles';

moment.locale('pt-br');
moment.updateLocale(moment.locale(), { invalidDate: '00/00/0000' });

// --- INTERFACES ---

export interface AccessProps {
  timestamp: string;
  action: string;
  user_name: string;
  pc_name: string;
  source: string;
  remote_client: string;
  session_type: string;
}

export interface AccessData {
  data: AccessProps[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface AccessSearchProps {
  page?: number;
  limit?: number;
  date_start?: string;
  date_end?: string;
  users?: string;
  actions?: string;
  pcNames?: string;
  sources?: string;
  remoteClients?: string;
  sessionTypes?: string;
}

export interface HeaderColumnProps {
  colSpan: number;
  aling: 'left' | 'inherit' | 'center' | 'right' | 'justify' | undefined;
  sort: boolean;
  fieldName: string;
  text: string;
  width?: string | '100%';
}

// --- DADOS ESTÁTICOS (Movidos para fora para evitar recriação) ---

const columns: HeaderColumnProps[] = [
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'timestamp', text: 'Data/Hora' },
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'action', text: 'Ação' },
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'user_name', text: 'Usuário' },
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'pc_name', text: 'Computador' },
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'source', text: 'Origem (Script)' },
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'remote_client', text: 'Cliente Remoto' },
  { colSpan: 1, aling: 'left', sort: false, fieldName: 'session_type', text: 'Sessão' },
];

// --- COMPONENTE FILHO: Tabela de Logs (Isolado) ---

interface LogTableProps {
  accessData: AccessData;
  loadingData: boolean;
  limit: number;
  currentPage: number;
  classes: any;
  onPaginationChange: (event: React.ChangeEvent<unknown>, page: number) => void;
}

const LogTable: React.FC<LogTableProps> = ({
  accessData,
  loadingData,
  limit,
  currentPage,
  classes,
  onPaginationChange,
}) => {
  const logs = accessData.data || [];
  const emptyRows = logs.length > 0 ? limit - logs.length : limit - 1;
  moment.locale('pt-br');

  const rowsSkeleton = Array.from({ length: limit }, (_, i) => i);

  return (
    <Box mt={2}>
      <Paper className={classes.tablePaper}>
        <Box p={1}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader className={classes.table} size="small" aria-label="table logs">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell key={index} width={column.width} colSpan={column.colSpan} align={column.aling}>
                      <Typography style={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: '1.5rem' }}>
                        {column.text}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingData ? (
                  rowsSkeleton.map(row => (
                    <TableRow key={`skeleton-${row}`}>
                      <TableCell style={{ padding: 2 }} colSpan={columns.length} height={33}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography
                        variant="subtitle1"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        Nenhum registro encontrado.{' '}
                        <Icon style={{ marginLeft: '8px' }}>sentiment_very_dissatisfied</Icon>
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => (
                    <TableRow hover tabIndex={-1} key={`row-${index}`}>
                      <TableCell align="left" style={{ minWidth: 150 }}>
                        {moment.utc(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}
                      </TableCell>
                      <TableCell align="left">{log.action}</TableCell>
                      <TableCell align="left">{log.user_name}</TableCell>
                      <TableCell align="left">{log.pc_name}</TableCell>
                      <TableCell align="left">{log.source}</TableCell>
                      <TableCell align="left">{log.remote_client}</TableCell>
                      <TableCell align="left">{log.session_type}</TableCell>
                    </TableRow>
                  ))
                )}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 33 * emptyRows }}>
                    <TableCell colSpan={columns.length} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <div className={classes.actions}>
        <Typography variant="caption" style={{ marginBottom: 4 }}>
          {`${accessData.pagination?.total || 0} Registros encontrados`}
        </Typography>

        <Pagination
          size="medium"
          count={accessData.pagination?.totalPages || 1}
          page={currentPage}
          variant="outlined"
          shape="rounded"
          disabled={(accessData.pagination?.totalPages || 1) <= 1}
          onChange={onPaginationChange}
        />
      </div>
    </Box>
  );
};

// --- COMPONENTE PAI ---

export default function MyAccesses() {
  const classes = styles();

  const [accessData, setAccessData] = useState<AccessData>({
    data: [],
    pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
  });

  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(parseInt(localStorage.getItem('search-logs-limit') || '100', 10));

  const [searchUsers, setSearchUsers] = useState<string>(localStorage.getItem('search-logs-users') || '');
  const [searchActions, setSearchActions] = useState<string>(localStorage.getItem('search-logs-actions') || '');
  const [searchPcNames, setSearchPcNames] = useState<string>(localStorage.getItem('search-logs-pc-names') || '');
  const [searchSources, setSearchSources] = useState<string>(localStorage.getItem('search-logs-sources') || '');
  const [searchRemoteClients, setSearchRemoteClients] = useState<string>(
    localStorage.getItem('search-logs-remote-clients') || '',
  );
  const [searchSessionTypes, setSearchSessionTypes] = useState<string>(localStorage.getItem('search-logs-types') || '');

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const [inputsValue, setInputsValue] = useState<string[]>([
    localStorage.getItem('search-logs-limit') || '100', // 0
    localStorage.getItem('search-logs-date-start') || '', // 1
    localStorage.getItem('search-logs-date-end') || '', // 2
    localStorage.getItem('search-logs-users') || '', // 3
    localStorage.getItem('search-logs-actions') || '', // 4
    localStorage.getItem('search-logs-pc-names') || '', // 5
    localStorage.getItem('search-logs-sources') || '', // 6
    localStorage.getItem('search-logs-remote-clients') || '', // 7
    localStorage.getItem('search-logs-types') || '', // 8
  ]);

  const parseFilterToArray = (value?: string) => {
    if (!value) return undefined;
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  };

  const getUserData = ({
    page = 1,
    limit = Number(inputsValue[0]),
    date_start = inputsValue[1],
    date_end = inputsValue[2],
    users = inputsValue[3],
    actions = inputsValue[4],
    pcNames = inputsValue[5],
    sources = inputsValue[6],
    remoteClients = inputsValue[7],
    sessionTypes = inputsValue[8],
  }: AccessSearchProps) => {
    setCurrentPage(page);
    setLimit(limit);
    let cancel: Canceler;

    if (!loadingData) {
      const params = {
        page,
        limit,
        startDate: date_start,
        endDate: date_end,
        users: parseFilterToArray(users),
        actions: parseFilterToArray(actions),
        pcNames: parseFilterToArray(pcNames),
        sources: parseFilterToArray(sources),
        remoteClients: parseFilterToArray(remoteClients),
        sessionTypes: parseFilterToArray(sessionTypes),
      };

      setLoadingData(true);

      api
        .get('ti/ad/log', {
          params,
          paramsSerializer: params => {
            const searchParams = new URLSearchParams();
            Object.keys(params).forEach(key => {
              const value = params[key];
              if (Array.isArray(value)) {
                value.forEach(val => searchParams.append(key, val));
              } else if (value !== undefined && value !== '') {
                searchParams.append(key, value);
              }
            });
            return searchParams.toString();
          },
          cancelToken: new Axios.CancelToken(c => {
            cancel = c;
          }),
        })
        .then(response => {
          setAccessData(response.data);
          setLoadingData(false);
        })
        .catch(error => {
          console.error(error);
          setSnackbarMsg('Erro ao buscar logs.');
          setSnackbarType('error');
          setOpenSnackbar(true);
          setLoadingData(false);
        });
    }
    return () => {
      if (cancel) cancel();
    };
  };

  useEffect(() => {
    let active = true;
    if (active) getUserData({});
    return () => {
      active = false;
    };
  }, []);

  const handleSnackbarClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };

  const handlePagination = (event: React.ChangeEvent<unknown>, page: number) => {
    if (page < 1 || page > (accessData.pagination?.totalPages || 1)) return;
    getUserData({ page });
  };

  const handleApplyFilter = (e: string[]) => {
    setInputsValue(e);
    setSearchUsers(e[3]);
    setSearchActions(e[4]);
    setSearchPcNames(e[5]);
    setSearchSources(e[6]);
    setSearchRemoteClients(e[7]);
    setSearchSessionTypes(e[8]);

    localStorage.setItem('search-logs--limit', e[0]);
    localStorage.setItem('search-logs--date-start', e[1]);
    localStorage.setItem('search-logs--date-end', e[2]);
    localStorage.setItem('search-logs--users', e[3]);
    localStorage.setItem('search-logs--actions', e[4]);
    localStorage.setItem('search-logs--pc-names', e[5]);
    localStorage.setItem('search-logs--sources', e[6]);
    localStorage.setItem('search-logs--remote-clients', e[7]);
    localStorage.setItem('search-logs--types', e[8]);

    getUserData({
      page: 1,
      limit: Number(e[0]),
      date_start: e[1],
      date_end: e[2],
      users: e[3],
      actions: e[4],
      pcNames: e[5],
      sources: e[6],
      remoteClients: e[7],
      sessionTypes: e[8],
    });
  };

  const filtersInputs = [
    {
      type: 'combobox',
      id: 'manager-accesses-device-limit',
      label: 'Limite',
      defaultValue: inputsValue[0],
      comboBoxItems: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    },
    {
      type: 'date',
      id: 'manager-accesses-date-start',
      label: 'Data (Inicio)',
      dateType: 'start',
      defaultValue: inputsValue[1],
    },
    {
      type: 'date',
      id: 'manager-accesses-date-end',
      label: 'Data (Fim)',
      dateType: 'end',
      defaultValue: inputsValue[2],
    },
    { type: 'text', id: 'users', label: 'Usuários', defaultValue: inputsValue[3] },
    { type: 'text', id: 'actions', label: 'Ações', defaultValue: inputsValue[4] },
    { type: 'text', id: 'pcNames', label: 'Computadores', defaultValue: inputsValue[5] },
    { type: 'text', id: 'sources', label: 'Origens', defaultValue: inputsValue[6] },
    { type: 'text', id: 'remoteClients', label: 'Clientes Remotos', defaultValue: inputsValue[7] },
    { type: 'text', id: 'sessionTypes', label: 'Tipos de Sessão', defaultValue: inputsValue[8] },
  ] as FiltersInputsProps[];

  return (
    <Box>
      <Filters onFilterApply={handleApplyFilter} inputs={filtersInputs} initialInputsValue={inputsValue} />
      <Box className={classes.root}>
        <Grid container className={classes.contentRoot}>
          <Grid item xs={12}>
            {/* COMPONENTE ISOLADO RENDERIZADO AQUI */}
            <LogTable
              accessData={accessData}
              loadingData={loadingData}
              limit={limit}
              currentPage={currentPage}
              classes={classes}
              onPaginationChange={handlePagination}
            />
          </Grid>
          <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
            <Alert severity={snackbarType} onClose={handleSnackbarClose}>
              {snackbarMsg}
            </Alert>
          </Snackbar>
        </Grid>
      </Box>
    </Box>
  );
}
